import React from 'react';
import 'react-tippy/dist/tippy.css';
import PropTypes from 'prop-types';

import ChipMapper from 'ggApp/shared/components/cards/Chips';

import { LowInStockIcon } from 'ggApp/shared/icons/LowInStock';
import { RentalActions } from 'ggApp/shared/components/cards/ProductDetailsCard/RentalActions';
import { Translator } from 'ggApp/modules/translator';

import {
    Container,
    Wrap,
    FavoriteButton,
    ChipsBlock,
    Name,
    CoreAttributes,
    ShareButton,
    ReviewIO,
    LowInStock,
} from './styled';

const ProductDetailsCard = ({
    className,
    productName,
    price,
    id,
    slug,
    oldPrice,
    tooltipText,
    selectedPlan,
    onCtaClick,
    ctaTk,
    selectedPropertyItemIndex,
    currentlyAvailable,
    propertiesList,
    withColorSelector,
    coreAttributes,
    promoChips,
    withFreeShipping,
    isFlexibleProduct,
    disableFavorites,
    rentalPlans,
    selectedMinDuration,
    deliveryEstimate,
    marketPrice,
    canBeShared,
    dataForSharingProduct,
    callbacksForSharingProduct,
    selectProductVariant,
    onFavoriteCb,
    mixPDP,
    currentVariant,
    productSku,
    productId,
    preorderProduct,
    registerProductDetailedImpressionV2,
    activeStore,
    longRevocationPeriodTestData,
}) => {
    const selectOption = (item) => {
        selectProductVariant(item.variantId);
    };

    const hasRentalPlans = rentalPlans && rentalPlans.length && rentalPlans.length > 0;

    const filteredChips = promoChips?.filter((chip) => chip.label !== 'LOW_IN_STOCK_LABEL');
    const lowInStockChip = promoChips?.find((chip) => chip.label === 'LOW_IN_STOCK_LABEL');

    return (
        <Container className={className}>
            <Wrap mixPDP={mixPDP}>
                {!disableFavorites ? (
                    <FavoriteButton id={id} slug={slug} onClickCallback={onFavoriteCb} />
                ) : null}
                {canBeShared ? (
                    <ShareButton
                        message={dataForSharingProduct.message}
                        link={dataForSharingProduct.link}
                        isFirst={disableFavorites}
                        onClick={callbacksForSharingProduct.onClick}
                        onShared={callbacksForSharingProduct.onShared}
                        onCancel={callbacksForSharingProduct.onCancel}
                    />
                ) : null}
                {!!promoChips && promoChips.length > 0 ? (
                    <ChipsBlock>
                        <ChipMapper
                            chips={filteredChips}
                            selectedRentalPlan={selectedPlan}
                            currentVariant={currentVariant}
                        />
                    </ChipsBlock>
                ) : null}
                <Name>{productName}</Name>
                {!!coreAttributes ? (
                    <CoreAttributes mixPDP={mixPDP}>{coreAttributes}</CoreAttributes>
                ) : null}
                {lowInStockChip ? (
                    <LowInStock>
                        <LowInStockIcon />
                        <Translator tk={lowInStockChip?.label} />
                    </LowInStock>
                ) : null}
                <RentalActions
                    mixPDP={mixPDP}
                    marketPrice={marketPrice}
                    hasRentalPlans={hasRentalPlans}
                    productSku={productSku}
                    productId={productId}
                    oldPrice={oldPrice}
                    price={price}
                    tooltipText={tooltipText}
                    currentlyAvailable={currentlyAvailable}
                    withFreeShipping={withFreeShipping}
                    deliveryEstimate={deliveryEstimate}
                    isFlexibleProduct={isFlexibleProduct}
                    selectedMinDuration={selectedMinDuration}
                    selectedPlan={selectedPlan}
                    withColorSelector={withColorSelector}
                    selectedPropertyItemIndex={selectedPropertyItemIndex}
                    propertiesList={propertiesList}
                    selectOption={selectOption}
                    onCtaClick={onCtaClick}
                    ctaTk={ctaTk}
                    currentVariant={currentVariant}
                    preorderProduct={preorderProduct}
                    onClickMixCard={registerProductDetailedImpressionV2}
                    longRevocationPeriodTestData={longRevocationPeriodTestData}
                />
            </Wrap>
            <ReviewIO mixPDP={mixPDP} withLink={activeStore?.code !== 'us'} />
        </Container>
    );
};

ProductDetailsCard.propTypes = {
    className: PropTypes.string,
    productName: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    price: PropTypes.string.isRequired,
    selectedPlan: PropTypes.shape({
        minimumMonths: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    rentalPlans: PropTypes.arrayOf(PropTypes.object),
    onCtaClick: PropTypes.func.isRequired,
    ctaTk: PropTypes.string.isRequired,
    selectedPropertyItemIndex: PropTypes.number.isRequired,
    currentlyAvailable: PropTypes.bool,
    propertiesList: PropTypes.arrayOf(PropTypes.object).isRequired,
    withColorSelector: PropTypes.bool,
    coreAttributes: PropTypes.string,
    promoChips: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
        }),
    ),
    withFreeShipping: PropTypes.bool,
    selectProductVariant: PropTypes.func.isRequired,
    slug: PropTypes.string.isRequired,
    oldPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    tooltipText: PropTypes.string,
    isFlexibleProduct: PropTypes.bool,
    disableFavorites: PropTypes.bool,
    selectedMinDuration: PropTypes.number,
    productSku: PropTypes.string.isRequired,
    productId: PropTypes.number.isRequired,
    canBeShared: PropTypes.bool,
    dataForSharingProduct: PropTypes.shape({
        message: PropTypes.string,
        link: PropTypes.string,
    }),
    callbacksForSharingProduct: PropTypes.shape({
        onClick: PropTypes.func,
        onShared: PropTypes.func,
        onCancel: PropTypes.func,
    }),
    deliveryEstimate: PropTypes.shape({}),
    marketPrice: PropTypes.string,
    onFavoriteCb: PropTypes.func,
    currentVariant: PropTypes.shape({}).isRequired,
    registerProductDetailedImpressionV2: PropTypes.func,
};

ProductDetailsCard.defaultProps = {
    className: undefined,
    ctaText: undefined,
    coreAttributes: undefined,
    currentlyAvailable: undefined,
    withColorSelector: undefined,
    promoChips: undefined,
    withFreeShipping: undefined,
    oldPrice: undefined,
    tooltipText: undefined,
    isFlexibleProduct: false,
    disableFavorites: false,
    selectedMinDuration: null,
    canBeShared: true,
    dataForSharingProduct: {
        link: '',
        message: '',
    },
    callbacksForSharingProduct: {
        onClick: () => {},
        onShared: () => {},
        onCancel: () => {},
    },
    marketPrice: undefined,
    onFavoriteCb: () => null,
    deliveryEstimate: {},
    rentalPlans: [],
    registerProductDetailedImpressionV2: () => {},
};

export default ProductDetailsCard;
